/**
 * 影视分页：3 页，与 Home 一致。当前展示 11 个子模块，每页按 slideStatus 控制锁；开放入口点击进入 Menu（from=yingshi&serial_id）。
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import lockedSvg from 'asset/img/locked.svg';
import { YINGSHI_IMAGE_NAMES } from './courseConfig';
import '../Home/Slide/index.scss';

const YINGSHI_SUB_LOCKED_TIP = '完成上一关卡即可解锁';

const IMAGE_LOADERS = {
  'menu4-1.webp': () => import('asset/img/menu4-1.webp'),
  'menu4-2.webp': () => import('asset/img/menu4-2.webp'),
  'menu4-3.webp': () => import('asset/img/menu4-3.webp'),
  'menu4-4.webp': () => import('asset/img/menu4-4.webp'),
  'menu5-1.webp': () => import('asset/img/menu5-1.webp'),
  'menu5-2.webp': () => import('asset/img/menu5-2.webp'),
  'menu5-3.webp': () => import('asset/img/menu5-3.webp'),
  'menu5-4.webp': () => import('asset/img/menu5-4.webp'),
  'menu6-1.webp': () => import('asset/img/menu6-1.webp'),
  'menu6-2.webp': () => import('asset/img/menu6-2.webp'),
  'menu6-3.webp': () => import('asset/img/menu6-3.webp'),
  'background4.webp': () => import('asset/img/background4.webp'),
  'background5.webp': () => import('asset/img/background5.webp'),
  'background6.webp': () => import('asset/img/background6.webp'),
};

const BACKGROUND_NAMES = ['background4.webp', 'background5.webp', 'background6.webp'];

/** 每页标题图：与主页横幅一致，用图片 */
const TITLE_LOADERS = {
  'title4.webp': () => import('asset/img/title4.webp'),
  'title5.webp': () => import('asset/img/title5.webp'),
  'title6.webp': () => import('asset/img/title6.webp'),
};
const TITLE_NAMES = ['title4.webp', 'title5.webp', 'title6.webp'];

/**
 * 影视每页、每个入口的宽高与上下左右位置，便于单独调试
 * 按 [type][入口下标] 配置，支持 width/height、top/left/right/bottom，单位建议 px 或 rem
 */
export const ENTRY_SIZE_CONFIG = {
  0: {
    0: { width: '220px', height: '220px', top: '150px', right: '0px' },
    1: { width: '200px', height: '160px', top: '285px', left: '10px' },
    2: { width: '220px', height: '160px', top: '410px', right: '-5px' },
    3: { width: '215px', height: '160px', top: '560px', left: '15px' },
  },
  1: {
    0: { width: '225px', height: '155px', top: '150px', right: '5px' },
    1: { width: '215px', height: '155px', top: '285px', left: '10px' },
    2: { width: '215px', height: '155px', top: '420px', right: '5px' },
    3: { width: '215px', height: '155px', top: '555px', left: '10px' },
  },
  2: {
    0: { width: '260px', height: '160px', top: '145px', right: '-10px' },
    1: { width: '205px', height: '160px', top: '340px', left: '0px' },
    2: { width: '225px', height: '160px', top: '530px', right: '20px' },
  },
};

const YingshiSlide = ({ type, isYingshiUnlocked, slideStatus, slideSerials, onImagesLoaded }) => {
  const navigate = useNavigate();
  const [images, setImages] = useState({});
  const [background, setBackground] = useState(null);
  const [titleSrc, setTitleSrc] = useState(null);
  const count = YINGSHI_IMAGE_NAMES[type]?.length || 0;
  const levelArray = Array.from({ length: count }, (_, idx) => idx);
  const sizeConfig = ENTRY_SIZE_CONFIG[type] || {};
  // 三页统一：按 slideStatus 控制，第一个默认开放，其余需上一子模块 500 关
  const isEntryOpen = (idx) => {
    if (Array.isArray(slideStatus) && slideStatus[idx] !== undefined) return !!slideStatus[idx];
    return true;
  };

  useEffect(() => {
    let isMounted = true;
    const loadImages = async () => {
      try {
        const bgLoader = IMAGE_LOADERS[BACKGROUND_NAMES[type]];
        const bgModule = bgLoader ? await bgLoader() : null;
        const titleLoader = TITLE_LOADERS[TITLE_NAMES[type]];
        const titleModule = titleLoader ? await titleLoader() : null;
        const menuImages = {};
        for (let idx = 0; idx < count; idx++) {
          const fileName = YINGSHI_IMAGE_NAMES[type][idx];
          const loader = IMAGE_LOADERS[fileName];
          if (loader) {
            const mod = await loader();
            menuImages[idx] = mod.default;
          }
        }
        if (isMounted) {
          setBackground(bgModule?.default || null);
          setTitleSrc(titleModule?.default || null);
          setImages(menuImages);
          if (onImagesLoaded) onImagesLoaded();
        }
      } catch (err) {
        console.error('YingshiSlide 加载图片失败:', err);
        if (onImagesLoaded) onImagesLoaded();
      }
    };
    loadImages();
    return () => { isMounted = false; };
  }, [type, count, onImagesLoaded]);

  return (
    <div className="menu-container yingshi-slide">
      {background && (
        <img src={background} className="background" loading="lazy" decoding="async" alt="" />
      )}
      {titleSrc && (
        <div className="yingshi-title">
          <img src={titleSrc} alt="" loading="lazy" decoding="async" />
        </div>
      )}
      <div className="menu-content">
        {levelArray.map((idx) => {
          const entry = sizeConfig[idx] || { width: '180px', height: '160px' };
          const style = {
            width: entry.width,
            height: entry.height,
            ...(entry.top != null && { top: entry.top }),
            ...(entry.left != null && { left: entry.left }),
            ...(entry.right != null && { right: entry.right }),
            ...(entry.bottom != null && { bottom: entry.bottom }),
          };
          const open = isEntryOpen(idx);
          const serialId = Array.isArray(slideSerials) && slideSerials[idx] != null ? slideSerials[idx] : null;
          const handleClick = () => {
            if (open && serialId != null) {
              navigate(`/menu?from=yingshi&serial_id=${serialId}&slide=${type}`);
              return;
            }
            if (!open) message.info(YINGSHI_SUB_LOCKED_TIP);
          };
          return (
            <div
              key={idx}
              className={idx === 0 && type === 0 ? 'enter-8' : `enter-${idx}`}
              style={style}
              onClick={handleClick}
              role="button"
              tabIndex={-1}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); } }}
              aria-label={open ? '影视子模块入口' : '影视子模块入口（未解锁）'}
            >
              {images[idx] && (
                <div className="item-wrap">
                  <img
                    src={images[idx]}
                    className="item"
                    loading="lazy"
                    decoding="async"
                    alt=""
                  />
                  {!open && <img src={lockedSvg} className="lock-icon" alt="" aria-hidden />}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default React.memo(YingshiSlide);
